import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Drawer,
  AppBar as MuiAppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Container,
  Tooltip,
  useMediaQuery,
  Switch,
  Button,
  Chip, // Add this
  CircularProgress,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  RocketLaunch as RocketLaunchIcon,
  AutoStories as AutoStoriesIcon,
  MonetizationOn as MonetizationOnIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Science as ScienceIcon,
  Inventory as InventoryIcon,
  DeviceHub as FlowIcon,
  VpnKey as VpnKeyIcon,
  ArrowBack as ArrowBackIcon,
  Extension as ExtensionIcon,
  DesignServices as BlueprintsIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { useThemeMode } from "../contexts/ThemeContext";
import { toast } from "sonner";
import ProjectSwitcher from "./ProjectSwitcher";
import WordLogo from "../assets/icons/BunyanLogo";
import WordLogoDark from "../assets/icons/BunyanLogoDark";
import BunyanTextLogo from "../assets/icons/BunyanTextLogo";
import BunyanTextLogoRTL from "../assets/icons/BunyanTextLogoRTL";
import BunyanTextLogoDark from "../assets/icons/BunyanTextLogoDark";
import BunyanTextLogoDarkRTL from "../assets/icons/BunyanTextLogoDarkRTL";
import tokenManager from "../utils/tokenManager";
import { useTranslation } from "react-i18next";
import { getContrastColor, useStoredTheme } from "@/utils/theme-context";
import { useOrganization } from "../contexts/OrganizationContext";

const drawerWidth = 320;

const AppBarStyled = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})(({ theme, open, isMobile }) => {
  const storedTheme = useStoredTheme();

  return {
    ...(theme.direction === "rtl"
      ? {
          left: 0,
          right: "auto",
        }
      : {
          left: "auto",
          right: 0,
        }),
    zIndex: theme.zIndex.drawer,
    transition: theme.transitions.create(["width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    border: "none",
    backgroundColor: storedTheme.header.backgroundColor[theme.palette.mode],
    color: storedTheme.header.textColor[theme.palette.mode],
    borderBottom: "none",
    boxShadow: "none",
    width: `${!isMobile ? "calc(100% - 71px)" : "100%"}`,
    ...(open && {
      transition: theme.transitions.create(["width"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  };
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

// Custom styled theme switch
const MaterialUISwitch = styled(Switch)(({ theme }) => {
  const storedTheme = useStoredTheme();
  const bg = storedTheme.inputSwitch.backgroundColor[theme.palette.mode];
  const isRTL = theme.direction === "rtl";

  return {
    width: 62,
    height: 34,
    padding: 7,
    "& .MuiSwitch-switchBase": {
      margin: 1,
      padding: 0,
      transform: isRTL
        ? "translateX(-6px) !important"
        : "translateX(6px) !important",
      "&.Mui-checked": {
        color: "var(--color-3)",
        transform: isRTL
          ? "translateX(-22px) !important"
          : "translateX(22px) !important",
        "& .MuiSwitch-thumb:before": {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
            "var(--color-3)",
          )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
        },
        "& + .MuiSwitch-track": {
          backgroundColor: bg,
          opacity: 0.5,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      backgroundColor: bg,
      width: 32,
      height: 32,
      "&:before": {
        content: "''",
        position: "absolute",
        width: "100%",
        height: "100%",
        insetInlineStart: 0,
        top: 0,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "var(--color-3)",
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
      },
    },
    "& .MuiSwitch-track": {
      opacity: 0.3,
      borderRadius: 20 / 2,
    },
  };
});

const DashboardLayout = ({
  children,
  title,
  showBackButton = false,
  fullHeight = false,
  hideNavigation = false,
  noPadding = false,
  noSidebarMargin = false,
  singleRenderedChildren = false,
  banner,
}) => {
  const { i18n, t } = useTranslation();
  const { currentUser, userProfile, logOut } = useAuth();
  const { canViewDeployments, canViewApiKey, isInferenceOnly } =
    usePermissions();
  const { mode, toggleTheme } = useThemeMode();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { loading, initialized, loadOrganizations } = useOrganization();
  const [open, setOpen] = useState(!isMobile && !hideNavigation);
  const [anchorEl, setAnchorEl] = useState(null);
  const isRTL = i18n.dir() === "rtl";
  const storedTheme = useStoredTheme();
  // Load organizations when component mounts
  useEffect(() => {
    let isMounted = true;

    if (
      !initialized &&
      isMounted &&
      localStorage.getItem("currentOrganizationId")
    ) {
      loadOrganizations();
    }

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [initialized, loadOrganizations]);

  if (singleRenderedChildren) return children;
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logOut();
      // navigate("/login");
      toast.success(t("auth.loggedOutSuccess"));
    } catch (error) {
      toast.error(t("auth.failedToLogout"));
      console.error(error);
    }
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      setOpen(false); // Close drawer on mobile after navigation
    }
  };

  const menuItems = [
    { text: t("sidebar.dashboard"), icon: <HomeIcon />, path: "/" },
    {
      text: t("sidebar.deployments"),
      icon: <RocketLaunchIcon />,
      path: "/deployments",
      visible: canViewDeployments,
    },
    {
      text: t("sidebar.documentCollections"),
      icon: <AutoStoriesIcon />,
      path: "/collections",
      visible: !isInferenceOnly,
    },
    {
      text: t("sidebar.aiCatalog"),
      icon: <InventoryIcon />,
      path: "/ai-catalog",
    },
    {
      text: t("sidebar.flows"),
      icon: <FlowIcon />,
      path: "/flows",
      visible: !isInferenceOnly,
    },
    {
      text: t("sidebar.playground"),
      icon: <ScienceIcon />,
      path: "/playground",
      // Keep the legacy route available for now, but do not expose it through
      // the sidebar based on a previously saved user preference.
      // path:
      //   localStorage.getItem("playground-preference") === "legacy"
      //     ? "/legacyplayground"
      //     : "/playground",
    },
    // BluePrints visible only for non-inference-only users, as per the original code logic
    {
      text: t("sidebar.bluePrints"),
      icon: <BlueprintsIcon />,
      path: "/blueprints",
      visible: !isInferenceOnly,
    },
    {
      text: t("sidebar.apiKeys"),
      icon: <VpnKeyIcon />,
      path: "/platform/security/api-keys",
      visible: canViewApiKey,
    },
    // Temporarily hide MCPs from the sidebar; keep the entry for re-enabling.
    // {
    //   text: t("sidebar.mcp"),
    //   icon: <ExtensionIcon />,
    //   path: "/MCPS",
    //   visible: !isInferenceOnly,
    // },
    { text: t("sidebar.settings"), icon: <SettingsIcon />, path: "/settings" },
    {
      text: t("sidebar.usageCost"),
      path: "/usage",
      icon: <MonetizationOnIcon />,
    },
  ].filter((item) => item.visible !== false);

  const SidebarItem = ({ icon, text, to, isActive, onClick, submenuItems }) => {
    const location = useLocation();
    const isCurrentRoute = location.pathname === to;
    const { mode } = useThemeMode();

    // Check if any submenu item is active
    const isSubmenuActive = submenuItems?.some(
      (item) => location.pathname === item.path,
    );

    const itemClickHandler = () => {
      handleMenuItemClick(to); // Use the general navigation handler
    };

    // Create cloned icons with consistent sizing
    const sizedIcon = React.cloneElement(icon, {
      sx: { width: 20, height: 20 },
    });

    return (
      <>
        <ListItem
          button
          onClick={itemClickHandler}
          sx={{
            mb: 0.5,
            mx: 0,
            paddingInlineStart: 2,
            paddingInlineEnd: 2,
            py: 1,
            borderRadius: "12px",
            position: "relative",
            backgroundColor:
              isCurrentRoute || isSubmenuActive
                ? storedTheme.sidebar.activeLinkBackgroundColor[mode]
                : "transparent",
            background:
              isCurrentRoute || isSubmenuActive
                ? storedTheme.sidebar.activeLinkBackgroundColor[mode]
                : "transparent",
            border: isCurrentRoute && "1px solid rgba(249, 249, 249, 0.30)",
            "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
              color: storedTheme.sidebar.textColor[mode],
            },
            "&:hover": {
              backgroundColor: storedTheme.sidebar.hoverBackgroundColor[mode],
              background: storedTheme.sidebar.hoverBackgroundColor[mode],
              "& .MuiListItemIcon-root": {
                color: storedTheme.sidebar.hoverTextColor[mode],
              },
              "& .MuiListItemText-primary": {
                color: storedTheme.sidebar.hoverTextColor[mode],
              },
              "& .MuiSvgIcon-root": {
                color: storedTheme.sidebar.hoverTextColor[mode],
              },
              "& .expand-icon": {
                color: storedTheme.sidebar.hoverTextColor[mode],
              },
            },
            "&.Mui-selected": {
              backgroundColor:
                storedTheme.sidebar.activeLinkBackgroundColor[mode],
              background: storedTheme.sidebar.activeLinkBackgroundColor[mode],
              "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
                color: storedTheme.sidebar.activeLinkTextColor[mode],
              },
              "&:hover": {
                backgroundColor: storedTheme.sidebar.hoverBackgroundColor[mode],
                background: storedTheme.sidebar.hoverBackgroundColor[mode],
                "& .MuiListItemIcon-root": {
                  color: storedTheme.sidebar.hoverTextColor[mode],
                },
                "& .MuiListItemText-primary": {
                  color: storedTheme.sidebar.hoverTextColor[mode],
                },
                "& .MuiSvgIcon-root": {
                  color: storedTheme.sidebar.hoverTextColor[mode],
                },
                "& .expand-icon": {
                  color: storedTheme.sidebar.hoverTextColor[mode],
                },
              },
            },
          }}
          selected={isCurrentRoute || isSubmenuActive}
        >
          <ListItemIcon
            sx={{
              minWidth: 40,
              "& svg": {
                color:
                  isCurrentRoute || isSubmenuActive
                    ? storedTheme.sidebar.activeLinkTextColor[mode]
                    : storedTheme.sidebar.textColor[mode],
                transition: "color 0.2s ease",
              },
            }}
          >
            {sizedIcon}
          </ListItemIcon>

          {isMobile ? (
            <>
              <ListItemText
                primary={text}
                sx={{
                  minWidth: 0,
                  "& .MuiTypography-root": {
                    color:
                      isCurrentRoute || isSubmenuActive
                        ? storedTheme.sidebar.activeLinkTextColor[mode]
                        : storedTheme.sidebar.textColor[mode],
                    fontSize: "15px",
                    fontWeight: 500,
                    transition: "color 0.2s ease",
                  },
                }}
              />
            </>
          ) : (
            <Box
              className="menu-text"
              sx={{
                marginInlineStart: 2,
                transition: "opacity 0.3s ease",
                opacity: 0,
                display: "block",
                flex: "1 1 auto",
                maxWidth: "100%",
                minWidth: 0,
                whiteSpace: "nowrap",
              }}
            >
              <ListItemText
                primary={text}
                sx={{
                  minWidth: 0,
                  "& .MuiTypography-root": {
                    color:
                      isCurrentRoute || isSubmenuActive
                        ? storedTheme.sidebar.activeLinkTextColor[mode]
                        : storedTheme.sidebar.textColor[mode],
                    fontSize: "15px",
                    fontWeight: 500,
                    transition: "color 0.2s ease",
                  },
                }}
              />
            </Box>
          )}
        </ListItem>
      </>
    );
  };

  return loading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <Box sx={{ display: "flex" }}>
      {!hideNavigation && (
        <AppBarStyled
          position="fixed"
          open={open}
          isMobile={isMobile}
          elevation={0}
        >
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerOpen}
                edge="start"
                sx={{ marginInlineEnd: 2, ...(open && { display: "none" }) }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {showBackButton && (
              <Box className="cursor-pointer me-2" onClick={() => navigate(-1)}>
                <ArrowBackIcon
                  className="rtl:scale-x-[-1]"
                  sx={{
                    color: storedTheme.header.textColor[theme.palette.mode],
                  }}
                />
              </Box>
            )}

            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                color: storedTheme.header.textColor[theme.palette.mode],
              }}
            >
              {title}
            </Typography>
            <Box sx={{ marginInlineEnd: 2 }}>
              {currentUser && <ProjectSwitcher />}
            </Box>

            {!isMobile && (
              <Tooltip
                title={
                  mode === "dark"
                    ? t("common.switchToLightMode")
                    : t("common.switchToDarkMode")
                }
              >
                <MaterialUISwitch
                  checked={mode === "dark"}
                  onChange={toggleTheme}
                  sx={{ marginInlineEnd: 1 }}
                />
              </Tooltip>
            )}

            {!isMobile && (
              <Tooltip title={t("common.account")}>
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  color="inherit"
                >
                  {userProfile?.photoURL ? (
                    <Avatar
                      src={userProfile.photoURL}
                      alt={userProfile.displayName || currentUser.email}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        bgcolor: storedTheme?.header?.iconColor[mode],
                        color: getContrastColor(
                          storedTheme?.header?.iconColor[mode] ||
                            "var(--color-8)",
                        ),
                      }}
                    >
                      {(userProfile?.displayName || currentUser?.email || "")
                        .charAt(0)
                        .toUpperCase()}
                    </Avatar>
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: isRTL ? "left" : "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: isRTL ? "left" : "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate("/settings");
                }}
              >
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                {t("common.profile")}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  handleLogout();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {t("common.logout")}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBarStyled>
      )}

      <Box
        sx={{
          width: hideNavigation ? 0 : isMobile ? 0 : "71px",
          flexShrink: 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          overflow: "visible",
          height: "100vh",
          zIndex: 1600,
          position: isMobile || hideNavigation ? "static" : "fixed",
          "&:hover": {
            width: hideNavigation ? 0 : isMobile ? 0 : drawerWidth,
            "& .MuiDrawer-paper": {
              width: hideNavigation ? 0 : isMobile ? drawerWidth : drawerWidth,
            },
            "& .word-logo": {
              display: "none",
            },
            "& .text-logo": {
              display: "block",
            },
          },
        }}
      >
        {!hideNavigation && (
          <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            anchor={isRTL ? "right" : "left"}
            open={open}
            onClose={handleDrawerClose}
            sx={{
              width: "100%",
              height: "100%",
              "& .MuiDrawer-paper": {
                width: isMobile ? drawerWidth : "71px",
                height: "100%",
                boxSizing: "border-box",
                position: "fixed",
                overflowY: "auto",
                borderRadius: 0,
                backgroundColor: storedTheme.sidebar.backgroundColor[mode],
                color: storedTheme.sidebar.textColor[mode],
                transition: theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                }),
                "&:hover": {
                  width: isMobile ? drawerWidth : drawerWidth,
                  "& .menu-text": {
                    opacity: 1,
                    display: "block",
                    pointerEvents: "auto",
                  },
                },
                zIndex: 1600,
              },
            }}
          >
            <DrawerHeader>
              <Box
                sx={{
                  p: 1,
                  width: "100%",
                }}
              >
                <Box
                  className="word-logo"
                  sx={{
                    transition: "opacity 0.3s ease",
                  }}
                >
                  {storedTheme.logo[mode] ? (
                    <img
                      src={storedTheme.logo[mode]}
                      alt="logo"
                      crossOrigin="anonymous"
                      style={{
                        height: "40px",
                        width: "50px",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <WordLogo height={40} width={50} />
                  )}
                </Box>
                <Box
                  className="text-logo"
                  sx={{
                    display: "none",
                    transition: "opacity 0.3s ease",
                  }}
                >
                  {storedTheme.wideLogo[mode] ? (
                    <img
                      src={storedTheme.wideLogo[mode]}
                      alt="wideLogo"
                      crossOrigin="anonymous"
                      style={{
                        height: "38px",
                        width: "145px",
                      }}
                    />
                  ) : mode === "light" ? (
                    isRTL ? (
                      <BunyanTextLogoRTL height={38} width={183} />
                    ) : (
                      <BunyanTextLogo height={38} width={183} />
                    )
                  ) : isRTL ? (
                    <BunyanTextLogoDarkRTL height={38} width={183} />
                  ) : (
                    <BunyanTextLogoDark height={38} width={183} />
                  )}
                </Box>
              </Box>
            </DrawerHeader>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "calc(100% - 64px)",
              }}
            >
              <List
                sx={{
                  p: 0,
                  width: "100%",
                  padding: "10px",
                }}
              >
                {menuItems.map((item) => (
                  <SidebarItem
                    key={item.text}
                    icon={item.icon}
                    text={item.text}
                    to={item.path}
                    isActive={location.pathname === item.path}
                    // hasSubmenu={item.hasSubmenu}
                    onClick={item.onClick}
                    submenuItems={item.submenuItems}
                  />
                ))}
              </List>

              {isMobile && (
                <Box
                  sx={{
                    borderTop: `1px solid ${
                      mode === "light" ? "var(--color-9)" : "var(--color-10)"
                    }`,
                    mt: "auto",
                    p: 2,
                    position: "sticky",
                    bottom: 0,
                    backgroundColor:
                      mode === "light" ? "var(--color-3)" : "var(--color-11)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box
                      onClick={handleMenu}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        borderRadius: 1,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor:
                            mode === "light"
                              ? "var(--color-7)"
                              : "var(--color-13)",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "var(--color-14)",
                          color: "var(--color-6)",
                        }}
                      >
                        {(userProfile?.displayName || currentUser?.email || "")
                          .charAt(0)
                          .toUpperCase()}
                      </Avatar>
                      <Typography
                        sx={{
                          marginInlineStart: 2,
                          color:
                            mode === "light"
                              ? "var(--color-1)"
                              : "var(--color-12)",
                          fontWeight: 500,
                        }}
                      >
                        Profile
                      </Typography>
                    </Box>

                    <Box
                      onClick={handleLogout}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        borderRadius: 1,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor:
                            mode === "light"
                              ? "var(--color-7)"
                              : "var(--color-13)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color:
                            mode === "light"
                              ? "var(--color-1)"
                              : "var(--color-12)",
                        }}
                      >
                        <LogoutIcon />
                      </Box>
                      <Typography
                        sx={{
                          marginInlineStart: 2,
                          color:
                            mode === "light"
                              ? "var(--color-1)"
                              : "var(--color-12)",
                          fontWeight: 500,
                        }}
                      >
                        Logout
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Drawer>
        )}
      </Box>

      {/* Main content area - adjust with drawer width when open */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          transition: theme.transitions.create(
            ["width", "margin-inline-start"],
            {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.standard,
            },
          ),
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          backgroundColor: storedTheme.header.backgroundColor[mode],
          border: "none",
          marginInlineStart:
            isMobile || noSidebarMargin || hideNavigation ? 0 : "71px",
          ...(isMobile && {
            width: "100%",
            transition: "none",
          }),
        }}
      >
        {!hideNavigation && <Toolbar />}
        {banner && <Box sx={{ zIndex: 10 }}>{banner}</Box>}
        <Box
          id="scrolling-parent"
          sx={{
            p: noPadding ? 0 : { xs: 1, sm: 2 },
            pt: noPadding ? 0 : { xs: "30px", sm: "16px" },
            flexGrow: 1,
            overflow: "auto",
            height: hideNavigation
              ? "100vh"
              : fullHeight
                ? `calc(100vh - ${isMobile ? 56 : 64}px)`
                : "auto",
            backgroundColor: storedTheme.general.backgroundColor[mode],
            color: storedTheme.general.textColor[mode],
            ...(isMobile && {
              paddingInlineStart: theme.spacing(1),
              paddingInlineEnd: theme.spacing(1),
            }),
          }}
        >
          {fullHeight ? (
            children
          ) : (
            <Container maxWidth="xl" disableGutters={noPadding}>
              {children}
            </Container>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
